import React, { useRef } from "react";
import { useHistory } from "react-router-dom";
import { Formik, Form, Field, ErrorMessage } from "formik";
import * as yup from "yup";
import { useQueryClient } from "react-query";
import { Button, Input } from "antd";
import styled from "styled-components";
import UIOutlet from "components/common/UIOutlet";
import { forgotPasswordValidation } from "services/apiSrv";
import { show_toast } from "utils/helpers";

// Styled components
const FormContainer = styled.div`
    max-width: 400px;
    margin: 0 auto;
    padding: 24px;
    display: flex;
    flex-direction: column;
    justify-content: center;
    align-items: center;
    min-height: calc(100vh - 200px);
`;

const PageHeader = styled.div`
    margin-bottom: 24px;
    text-align: center;
`;

const Title = styled.h1`
    font-size: 20px;
    font-weight: 600;
    color: #001F5C;
    margin: 0 0 6px 0;
    letter-spacing: -0.02em;
`;

const Divider = styled.div`
    width: 40px;
    height: 3px;
    background: #EF3340;
    border-radius: 2px;
    margin: 0 auto;
`;

const StyledFormItem = styled.div`
    margin-bottom: 16px;
    width: 100%;

    .form-label {
        font-size: 12px;
        font-weight: 500;
        color: #595959;
        height: 20px;
        display: block;
        margin-bottom: 4px;
    }

    .error-message {
        color: #ff4d4f;
        font-size: 12px;
        margin-top: 4px;
    }
`;

const OTPContainer = styled.div`
    display: flex;
    gap: 8px;
    justify-content: center;
`;

const OTPInput = styled(Input)`
    width: 48px;
    height: 48px;
    font-size: 24px;
    text-align: center;
    border-radius: 6px;
    border-color: #e8e8e8;

    &:focus,
    &:hover {
        border-color: #00B8D4;
    }
`;

const ActionButtons = styled.div`
    display: flex;
    justify-content: flex-end;
    gap: 12px;
    margin-top: 24px;
    width: 100%;
`;

const SubmitButton = styled(Button)`
    background: #001F5C;
    border-color: #001F5C;
    height: 36px;
    padding: 0 24px;
    font-weight: 500;
    font-size: 14px;

    &:hover,
    &:focus {
        background: #00306B;
        border-color: #00306B;
    }
`;

const CancelButton = styled(Button)`
    height: 36px;
    padding: 0 24px;
    font-weight: 500;
    font-size: 14px;
    border-color: #d9d9d9;
    color: #595959;

    &:hover,
    &:focus {
        border-color: #00B8D4;
        color: #00B8D4;
    }
`;

// Validation schema for 4-digit code
const validationSchema = yup.object({
    code: yup
        .string()
        .length(4, "Code must be exactly 4 digits")
        .matches(/^\d{4}$/, "Code must be a 4-digit number")
        .required("Code is required"),
});

const VerifyCode = () => {
    const history = useHistory();
    const queryClient = useQueryClient();
    const inputRefs = useRef([]); // Refs for OTP input fields

    // Retrieve forgotPasswordSystemRef from localStorage
    const forgotPasswordSystemRef = localStorage.getItem("passRef") || "";

    // Handle input change for OTP fields
    const handleInputChange = (index, value, setFieldValue, values) => {
        if (/^\d$/.test(value) || value === "") {
            const newCode = values.code.split("");
            newCode[index] = value;
            setFieldValue("code", newCode.join(""));

            // Move to next input if a digit is entered
            if (value && index < 3) {
                inputRefs.current[index + 1].focus();
            }
        }
    };

    // Handle key down for backspace and navigation
    const handleKeyDown = (index, e, setFieldValue, values) => {
        if (e.key === "Backspace" && !values.code[index] && index > 0) {
            inputRefs.current[index - 1].focus();
        }
    };

    // Handle form submission
    const handleSubmit = async (values, { setSubmitting, setErrors }) => {
        try {
            await queryClient.fetchQuery({
                queryKey: ["verifyCode", values.code, forgotPasswordSystemRef],
                queryFn: () => forgotPasswordValidation(values.code, forgotPasswordSystemRef),
            });
            show_toast("Code verified successfully.", "success");
            history.push("/reset-password");
        } catch (error) {
            console.error("Error verifying code", error);
            setErrors({ code: error?.message || "Invalid code or error occurred" });
            show_toast(error?.message || "Failed to verify code. Please try again.", "error");
            setSubmitting(false);
        }
    };

    return (
        <UIOutlet
            logoSrc="/icon.png"
            bannerTitle="Verify Your Code"
            bannerDescription="Enter the 4-digit code sent to your email to proceed with password reset"
        >
            <FormContainer>
                <PageHeader>
                    <Title>Check Your Email</Title>
                    <Divider />
                </PageHeader>

                <Formik
                    initialValues={{ code: "" }}
                    validationSchema={validationSchema}
                    onSubmit={handleSubmit}
                >
                    {({ isSubmitting, setFieldValue, values }) => (
                        <Form style={{ width: "100%" }}>
                            <StyledFormItem>
                                <label className="form-label">
                                    Enter Code <span style={{ color: "#ff4d4f" }}>*</span>
                                </label>
                                <OTPContainer>
                                    {[0, 1, 2, 3].map((index) => (
                                        <OTPInput
                                            key={index}
                                            maxLength={1}
                                            value={values.code[index] || ""}
                                            onChange={(e) =>
                                                handleInputChange(index, e.target.value, setFieldValue, values)
                                            }
                                            onKeyDown={(e) =>
                                                handleKeyDown(index, e, setFieldValue, values)
                                            }
                                            ref={(el) => (inputRefs.current[index] = el)}
                                        />
                                    ))}
                                </OTPContainer>
                                <ErrorMessage
                                    name="code"
                                    component="div"
                                    className="error-message"
                                />
                            </StyledFormItem>

                            <ActionButtons>
                                <CancelButton onClick={() => history.push("/forgot-password")}>
                                    Back to Forgot Password
                                </CancelButton>
                                <SubmitButton
                                    type="primary"
                                    htmlType="submit"
                                    loading={isSubmitting}
                                    disabled={isSubmitting}
                                >
                                    {isSubmitting ? "Processing..." : "Submit"}
                                </SubmitButton>
                            </ActionButtons>
                        </Form>
                    )}
                </Formik>
            </FormContainer>
        </UIOutlet>
    );
};

export default VerifyCode;